// shared/hooks/useCurrentUser.ts
import { auth } from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { getEndUserInfoWith } from '@jaymathew/ui-shared/apis/User'
import { useGetUser } from 'queryHooks/users/useGetUser'
import type { User } from 'resources/types'
import { useSelectedCompany } from './useSelectedCompany'

export const QK = (companyId: string) => ['currentUser', companyId] as const

/**
 * Canonical fetcher for the *current signed-in user* scoped to a company.
 * Uses your existing backend helper.
 */
export async function fetchCurrentUser(companyId: string): Promise<User> {
	if (!auth.currentUser) {
		throw new Error('Not signed in')
	}

	// Your shared API already returns the current end-user for the company
	const user = await getEndUserInfoWith<User>(companyId)
	if (!user) {
		throw new Error('Current user profile not found')
	}
	return user
}

/**
 * Hook version that keeps using your existing useGetUser, but
 * with a company-scoped query key and infinite freshness.
 */
export function useCurrentUser() {
	const { currentCompany } = useSelectedCompany()
	const companyId = currentCompany?.id

	const q = useGetUser({
		params: {
			userUID: auth.currentUser?.uid ?? '',
		},
		overrides: {
			queryKey: companyId ? QK(companyId) : ['currentUser', 'none'],
			enabled: !!companyId && !!auth.currentUser,
			staleTime: Infinity,
			gcTime: Infinity,
		},
	})

	return {
		companyId,
		currentUser: q.data,
		...q, // { data, isLoading, refetch, ... }
	}
}
